import styled from "@emotion/styled";
import { useConnection } from "@solana/wallet-adapter-react";
import { BaseButton, black, greenSecondary, mediaQueries, useViewportWidth } from "@stepfinance/react";
import type { MenuProps } from "antd";
import { Menu } from "antd";
import React, { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

import { ReactComponent as CopyIcon } from "../../assets/copy.svg";
import { useEnrichedWallet } from "../../contexts/enrichedWallet";
import { useLanguageContext } from "../../contexts/language/language";
import { useAddressInputStore } from "../../hooks/stores/addressInputStore";
import { useStepAuthStore } from "../../hooks/stores/stepAuth";
import { notify } from "../../utils/notifications";
import { ExplorerLinkDisplay } from "../ExplorerLink/ExplorerLinkDisplay";
import { activeWalletIcon, WatchedWalletIcon } from "../Icons";
import { useWalletModal } from "../WalletModal/useWalletModal";
import { AddressInput, watchWallet } from "./AddressInput";
import { appBarMenuPopoverStyles } from "./SettingsMenu";
import { WalletMenuButton } from "./WalletMenuButton";

export const WalletMenuConnected: React.FC = () => {
  const { disconnect, watching, publicKey, wallet } = useEnrichedWallet();
  const { setVisible } = useWalletModal();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentViewport } = useViewportWidth();
  const { connection } = useConnection();
  const address = useAddressInputStore((state) => state.address);
  const deleteJwt = useStepAuthStore((state) => state.deleteJwt);

  const {
    currentLanguageData: {
      pages: {
        defiOnSolana: { notifications },
        allPages: { topBar },
      },
    },
  } = useLanguageContext();

  const onTagClick = useCallback(() => {
    if (publicKey) {
      const stringifiedPublicKey = publicKey.toBase58();
      void navigator.clipboard.writeText(stringifiedPublicKey);
      notify({
        message: notifications.copiedAddress.message,
        type: "success",
        description: stringifiedPublicKey,
        icon: <CopyIcon color={greenSecondary} height={24} width={24} />,
      });
    }
  }, [notifications.copiedAddress.message, publicKey]);

  const onDisconnectWallet = useCallback(() => {
    setSearchParams("");
    deleteJwt();
    void disconnect();
  }, [disconnect, setSearchParams]);

  const onWatchClick = useCallback(
    () => watchWallet(address, connection, wallet, searchParams, setSearchParams),
    [address, connection, searchParams, setSearchParams, wallet],
  );

  return (
    <UserWalletMenu>
      <TopRow>
        <div>
          {watching ? <WatchedWalletIcon /> : wallet?.adapter ? activeWalletIcon(wallet?.adapter) : null}
          {publicKey && <ExplorerLinkDisplay type="address" address={publicKey} addressLength={7} />}
        </div>
        <div>
          <BaseButton
            icon={<CopyIcon />}
            size="small"
            onClick={onTagClick}
            style={{ display: "flex", alignItems: "center" }}
          />
        </div>
      </TopRow>
      <BottomRow>
        <WalletMenuButton
          onClick={() =>
            setTimeout(() => {
              setVisible(true);
            }, 100)
          }
        >
          {topBar.walletButton.changeWallet}
        </WalletMenuButton>
        <WalletMenuButton onClick={onDisconnectWallet}>{topBar.walletButton.disconnectWallet}</WalletMenuButton>
      </BottomRow>
      {currentViewport === "Mobile" && (
        <WatchWalletRow>
          {topBar.watchAnotherWallet}
          <AddressInput />
          <WalletMenuButton onClick={onWatchClick}>{topBar.walletButton.watchWallet}</WalletMenuButton>
        </WatchWalletRow>
      )}
    </UserWalletMenu>
  );
};

const WatchWalletRow = styled.div`
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;

  .ant-input {
    width: 100% !important;
  }

  .ant-input-affix-wrapper {
    background: ${black};
  }
`;

const UserWalletMenu = styled(Menu)<MenuProps>`
  ${appBarMenuPopoverStyles}
  border-radius: 8px;
  margin: -12px -16px;

  ${mediaQueries[3]} {
    margin: -12px 0px;
  }
  ${mediaQueries[4]} {
    margin: -12px 0px;
  }
  padding: 10px;
  width: 339px;
  div {
    width: 100%;
    svg {
      width: 16px;
      height: 16px;
      margin: 0 auto;
      align-self: center;
    }
  }
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${black};
  margin-bottom: 10px;
  padding: 0 10px;
  border-radius: 8px;
  > div {
    display: flex;
    img {
      padding: 8px;
      max-width: 30px;
    }
  }
  div {
    height: 32px;
    a {
      padding-left: 10px;
      line-height: 32px;
    }
  }
  div:last-of-type {
    display: flex;
    justify-content: flex-end;
    button {
      background: none;
      color: #ffffff;
      height: 32px;
      &:hover {
        background: none !important;
        svg {
          color: #06d6a0;
        }
      }
    }
  }
`;
const BottomRow = styled.div`
  display: flex;
  gap: 10px;
  button {
    width: 150px;
    font-size: 10px;
  }
`;
